import { useEffect, useState } from 'react';
import './ui.css';

/** Copies text, saying whether it worked (a browser may refuse, e.g. without permission). */
export async function copyText(text: string, clipboard: Pick<Clipboard, 'writeText'> | undefined = globalThis.navigator?.clipboard): Promise<boolean> {
  if (!clipboard) return false;
  try {
    await clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** How long "copied" shows. */
export const COPIED_MS = 1500;

/**
 * An address (or link) as one big button: click anywhere on it to copy. No "Copy" word: the
 * copy icon turns into an ember check for a moment, and screen readers hear "Copied".
 */
export function CopyAddress({ value, what = 'address' }: { value: string; what?: string }) {
  const [result, setResult] = useState<'idle' | 'copied' | 'failed'>('idle');

  useEffect(() => {
    if (result === 'idle') return;
    const timer = setTimeout(() => setResult('idle'), COPIED_MS);
    return () => clearTimeout(timer);
  }, [result]);

  return (
    <>
      <button
        className={result === 'copied' ? 'copy copied' : 'copy'}
        type="button"
        aria-label={`Copy the ${what} ${value}`}
        onClick={() => void copyText(value).then((ok) => setResult(ok ? 'copied' : 'failed'))}
      >
        <span className="copy-value">{value}</span>
        {result === 'copied' ? <CheckIcon /> : <CopyIcon />}
      </button>
      <span className="sr-only" aria-live="polite">
        {result === 'copied' ? 'Copied' : result === 'failed' ? "Couldn't copy it: select it and copy it by hand" : ''}
      </span>
    </>
  );
}

function CopyIcon() {
  return (
    <svg className="copy-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="copy-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M5 12.5 10 17.5 19 7" />
    </svg>
  );
}
